import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink } from "react-router-dom";
import { usePaymentsQuery } from "@/partner/entities/payments";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat, useAbility } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import { PagePaths } from "@/partner/shared/constants/routes";
import { BuyerEmailCell } from "@/partner/shared/components/BuyerEmailCell";
import { buildPaymentsFilter } from "../model/buildPaymentsFilter";
import {
  PAYMENT_STATUS_COLOR,
  PAYMENT_STATUS_KEY,
} from "../model/paymentStatus";
import { PaymentDetailsDialog } from "./PaymentDetailsDialog";
import { RefundButton } from "./RefundButton";
import {
  type PaymentsListQueryType,
  PaymentStatusType,
} from "@/types/graphql/partner.generated";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";

type PaymentRow = PaymentsListQueryType["payments"]["nodes"][number];

// Ключи локализации типа продукта (`products.type.*`) — тот же маппинг, что
// в списке покупок.
const PRODUCT_TYPE_KEY: Record<string, string> = {
  PPV: "ppv",
  SUBSCRIPTION: "subscription",
};

export const PaymentsList: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = usePaymentsQuery({
    // page сбрасывается в 0 самим FilterProvider при любом изменении
    // filter[...] (см. changeParams), отдельного сброса здесь не нужно.
    variables: { page, rowsPerPage, filter: buildPaymentsFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.payments?.nodes ?? [];
  const count = data?.payments?.count ?? 0;

  // Диалог деталей открывается локальным useState, как в списке покупок:
  // список read-only, ссылку на конкретный платёж никто не шэрит. Хранится
  // id, а не сама строка: после возврата (RefundButton) список обновляется
  // через refetchQueries, и карточка должна подхватить свежие refunds/статус
  // вместо снимка строки на момент клика.
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );
  const selectedPayment =
    rows.find((row) => row.id === selectedPaymentId) ?? null;

  const ability = useAbility();
  const canRefund = ability.can("create", "Refund");

  const emptyValue = t("common.emptyDash");

  const formatStatus = (row: PaymentRow): string =>
    t(`payments.status.${PAYMENT_STATUS_KEY[row.status]}`);

  const formatDate = (value: string, format: "short" | "time"): string =>
    interpolationFormat(value, format, i18n.language);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("payments.list.empty")} />;
  }

  return (
    <>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>{t("payments.list.amountColumn")}</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {t("payments.list.organizationAmountColumn")}
                  <Tooltip title={t("payments.amountHint")}>
                    <InfoOutlinedIcon color="disabled" fontSize="small" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>{t("payments.list.providerColumn")}</TableCell>
              <TableCell>{t("payments.list.buyerColumn")}</TableCell>
              <TableCell sx={{ minWidth: 240 }}>
                {t("payments.list.productColumn")}
              </TableCell>
              <TableCell>{t("payments.list.statusColumn")}</TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                {t("payments.list.createdAtColumn")}
              </TableCell>
              {canRefund && (
                <TableCell align="right">
                  {t("common.actions.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => setSelectedPaymentId(row.id)}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {formatAmountObject(row.amount)}
                  </Typography>
                </TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="body2" noWrap>
                    {formatAmountObject(
                      row.organizationAmountInOrganizationCurrency,
                      emptyValue
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" noWrap>
                    {t(PAYMENT_PROVIDER_LABEL_KEY[row.provider])}
                  </Typography>
                </TableCell>

                {/* Без email ссылка не рисуется: кликабельное «—» бессмысленно. */}
                <TableCell sx={{ maxWidth: 220 }}>
                  <BuyerEmailCell
                    email={row.buyerEmail}
                    userId={row.userId}
                    emptyValue={emptyValue}
                  />
                </TableCell>

                <TableCell sx={{ minWidth: 240 }}>
                  {row.product ? (
                    <>
                      <MuiLink
                        component={RouterLink}
                        color="textPrimary"
                        target="_blank"
                        to={`${PagePaths.products}?tab=edit&id=${row.product.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.product.title}
                      </MuiLink>
                      {/* Подпись типа продукта под названием, как в списке
                        покупок (референс ProductPurchasesList.js:198-205). */}
                      <Typography
                        variant="caption"
                        component="div"
                        color="textSecondary"
                        noWrap
                      >
                        {t(
                          `products.type.${PRODUCT_TYPE_KEY[row.product.type]}`
                        )}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2">{emptyValue}</Typography>
                  )}
                </TableCell>

                <TableCell>
                  {/* Статус это вход в деталку, как CrudLink у референса. */}
                  <Chip
                    size="small"
                    color={PAYMENT_STATUS_COLOR[row.status]}
                    label={formatStatus(row)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedPaymentId(row.id);
                    }}
                  />
                </TableCell>

                <TableCell align="right" sx={{ width: 160 }}>
                  <Typography variant="body2" color="textPrimary" noWrap>
                    {formatDate(row.createdAt, "short")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {formatDate(row.createdAt, "time")}
                  </Typography>
                </TableCell>

                {canRefund && (
                  <TableCell align="right">
                    {row.status === PaymentStatusType.SucceededType && (
                      <RefundButton
                        paymentId={row.id}
                        provider={row.provider}
                        defaultAmount={row.amount.value}
                      />
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage={t("common.pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("common.pagination.displayedRows", {
              from,
              to,
              count:
                count !== -1 ? count : t("common.pagination.moreThan", { to }),
            })
          }
        />
      </Card>

      <PaymentDetailsDialog
        open={Boolean(selectedPaymentId)}
        payment={selectedPayment}
        onClose={() => setSelectedPaymentId(null)}
      />
    </>
  );
};
